import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import ReloadButton from "ui-components/reload-button/reloadButton";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { userSelector } from "../../../slices/user/userSlice";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import { Routes, terminalNumberParamName } from "app/route/RoutesConfig";
import { isPickupAssignmentV2Allowed } from "./allowedTerminals";
import DraggableList from "ui-components/drag-n-drop/draggableList";
import { ManifestStopGroup } from "./delivery/manifestStopGroup";
import { PickupGroupCard } from "./delivery/pickupGroupCard";
import PickupDetails from "../../common/pickup-details/pickupDetails";
import { getPickup, pickupDetailsSelector } from "../../../slices/pickup/pickupDetailsSlice";
import { getPickupDetails } from "../../../services/pickups";
import SplitPanel, { SplitPanelSection } from "ui-components/split-panel/splitPanel";
import { FilterComponent } from "./filters/filters";
import TabsComponent from "./tabs/tabs";
import { UnassignedPickupStopGroup } from "./delivery/unassignedPickupStopGroup";
import AssignDriverBar from "./filters/assignDriver";
import { getPluralOrSingular } from "../utils/pickupLogUtils";
import "./pickupManagement.scss";
import { ReactComponent as ExpandAllIcon } from "images/expand_all.svg";
import { ReactComponent as CollapseAllIcon } from "images/collapse-all.svg";
import { unassignedPickupSelector } from "slices/pickup/unassignedPickupSlice";
import { UnassignedPickupState } from "slices/pickup/UnassignedPickupState";
import {
  usePickupAssignmentDriversColumn,
  filterDriverGroupsBySearch,
  filterDriverGroupsByTab,
  DriverGroup,
  DriverTabFilter,
  Stop,
} from "./hooks/usePickupAssignmentDriversColumn";
import { DriverColumnNestExpandContext } from "./driverColumnNestExpandContext";
import { useAssignDrivers } from "./hooks/useAssignDrivers";
import { PickupAssignRequestModel } from "app/data/pickup/models";
import { PickupStatus } from "app/data/common/route";
import type { Pickup } from "slices/pickup/UnassignedPickupState";
import {
  UnassignedPickup,
  UnassignedRoute,
  UnassignedStopGroup,
  aggregateUnassignedGroupStatus,
  staticRoutePickupGroupKey,
} from "./unassignedPickupTypes";
import { UnassignedStopNestExpandContext } from "./unassignedStopNestExpandContext";
import PickupAssignmentMapPanel from "./PickupAssignmentMapPanel";
import {
  PickupAssignmentMapSyncContext,
  usePickupAssignmentMapSyncState,
} from "./pickupMapSyncContext";
import { sanitizePickupDisplayText } from "./sanitizePickupDisplay";

const statusColorMapping = {
  AVAILABLE: '#14870A',
  ON_DUTY: '#FFA500',
  OFF_DUTY: '#C43232',
};

type DispatchColumns = {
  drivers: DriverGroup[];
  unassigned: UnassignedRoute[];
};

/** Expand-all only applies to drivers with assigned manifest (delivery) or pickup work. */
function driverHasAssignedPickupOrDelivery(driver: DriverGroup): boolean {
  return (
    driver.stops.length > 0 ||
    driver.numberOfPickups > 0 ||
    driver.numberOfDeliveries > 0
  );
}

const formatTime = (time: string) =>
  new Date(`1970-01-01T${time}`).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });

function joinContactPartsForUnassigned(parts: (string | undefined | null)[]): string {
  const s = parts
    .map((x) => {
      if (x == null) return null;
      const t = String(x).trim();
      if (t === "" || t.toLowerCase() === "null" || t.toLowerCase() === "undefined") {
        return null;
      }
      return t;
    })
    .filter((x): x is string => Boolean(x));
  return s.length ? s.join(" · ") : "—";
}

function sanitizeAddrToken(v: string | undefined): string {
  if (v == null) return "";
  const t = String(v).trim();
  if (t.toLowerCase() === "null" || t.toLowerCase() === "undefined") return "";
  return t;
}

function consigneeAddressLineFromPickup(pickup: Pickup): string {
  const p1 = sanitizeAddrToken(pickup.consigneeAddress1);
  const city = sanitizeAddrToken(pickup.consigneeCity);
  const state = sanitizeAddrToken(pickup.consigneeState);
  const zip = sanitizeAddrToken(pickup.consigneePostalCode);
  const line = [p1, city, state, zip].filter(Boolean).join(", ");
  return line || "—";
}

function rollsPalletsFromPickupItems(pickup: Pickup): { rolls: number; pallets: number } {
  let rolls = 0;
  let pallets = 0;
  for (const it of pickup.itemsList ?? []) {
    const u = it.units ?? 0;
    const pt = (it.packageType ?? "").toUpperCase();
    if (pt === "ROLL") rolls += u;
    else if (pt === "PALLET") pallets += u;
  }
  return { rolls, pallets };
}

function mapPickupToUnassigned(pickup: Pickup): UnassignedPickup {
  const probillRaw = pickup.probillNumber;
  let probillNumber: number | null = null;
  if (probillRaw != null && String(probillRaw).trim() !== "") {
    const n = Number(probillRaw);
    if (Number.isFinite(n) && n > 0) probillNumber = n;
  }

  const { rolls, pallets } = rollsPalletsFromPickupItems(pickup);
  const itemLines = (pickup.itemsList ?? []).map((it) => ({
    packageType: it.packageType,
    units: it.units,
    yards: it.yards ?? undefined,
    weight: it.weight,
    length: it.length,
    width: it.width,
    height: it.height,
    diameter: it.diameter,
    ratingClass: it.ratingClass,
    commodityCodeDescription: it.commodityCodeDescription ?? undefined,
    notes: it.notes,
    sidemark: it.sidemark,
    description: it.description ?? undefined,
  }));
  const datePart =
    pickup.pickupDate && moment(pickup.pickupDate).isValid()
      ? moment(pickup.pickupDate).format("MMM D, YYYY")
      : "—";
  const pickupWindowLine = `${datePart} · ${formatTime(pickup.pickupReadyTime)} – ${formatTime(pickup.pickupCloseTime)}`;
  const consigneeAddressLine = consigneeAddressLineFromPickup(pickup);
  const shipperContactLine = joinContactPartsForUnassigned([
    pickup.shipperMobilePhone,
    pickup.shipperEmail,
  ]);
  const consigneeContactLine = joinContactPartsForUnassigned([
    pickup.consigneePhone,
    pickup.consigneeEmail,
  ]);

  return {
    id: pickup.pickupId,
    title: pickup.shipperName,
    address: `${pickup.shipperAddress1}, ${pickup.shipperCity}, ${pickup.shipperState} ${pickup.shipperPostalCode}`,
    timeRange: `${formatTime(pickup.pickupReadyTime)} - ${formatTime(pickup.pickupCloseTime)}`,
    pickupDateForTiming: pickup.pickupDate,
    readyTimeForTiming: pickup.pickupReadyTime,
    closeTimeForTiming: pickup.pickupCloseTime,
    type: "Pickup",
    puNumber: pickup.pickupNumber,
    consigneeName: sanitizePickupDisplayText(pickup.consigneeName),
    pieces: pickup.totalUnits,
    weight: String(pickup.totalWeight),
    eta: pickup.pickupDate,
    shipperName: sanitizePickupDisplayText(pickup.shipperName),
    date: pickup.pickupDate,
    status: [pickup.pickupStatus],
    probillStatus: null,
    zip: pickup.shipperPostalCode,
    probillNumber,
    rollsUnits: rolls,
    palletsUnits: pallets,
    totalItemsWeight: pickup.totalWeight,
    pickupWindowLine,
    shipperContactLine,
    consigneeAddressLine,
    consigneeContactLine,
    itemLines,
  };
}

function groupRoutePickupsIntoStops(routeId: string, pickups: Pickup[]): UnassignedStopGroup[] {
  const buckets = new Map<string, Pickup[]>();
  for (const p of pickups) {
    const k = staticRoutePickupGroupKey(p);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k)!.push(p);
  }
  const groups: UnassignedStopGroup[] = [];
  let idx = 0;
  for (const [, list] of buckets) {
    list.sort((a, b) =>
      a.pickupNumber.localeCompare(b.pickupNumber, undefined, { numeric: true })
    );
    const ui = list.map(mapPickupToUnassigned);
    const safeKey = staticRoutePickupGroupKey(list[0])
      .replace(/[^\w-]+/g, "-")
      .slice(0, 48);
    const geo = list[0]?.shipperGeolocation;
    groups.push({
      id: `unassign-stop-${routeId}-${idx}-${safeKey}`,
      pickups: ui,
      groupStopStatus: aggregateUnassignedGroupStatus(ui),
      lat: geo?.lat,
      lng: geo?.lng,
    });
    idx += 1;
  }
  return groups;
}

const mapUnassignedRoutes = (data: { staticRouteGroups?: unknown[] }): UnassignedRoute[] => {
  if (!data?.staticRouteGroups?.length) return [];

  return data.staticRouteGroups.map((route: any) => {
    const routeId = route.staticRouteName.toLowerCase().replace(/\s+/g, "-");
    const pickups: Pickup[] = route.pickups ?? [];
    return {
      id: routeId,
      name: route.staticRouteName,
      count: pickups.length,
      stops: groupRoutePickupsIntoStops(routeId, pickups),
    };
  });
};

function collectPickupIdsForAssign(stops: Array<Stop | UnassignedPickup | UnassignedStopGroup>): string[] {
  const ids: string[] = [];
  for (const s of stops) {
    if (!("kind" in s)) {
      if ("pickups" in s && Array.isArray((s as UnassignedStopGroup).pickups)) {
        for (const p of (s as UnassignedStopGroup).pickups) {
          ids.push(p.id);
        }
      } else {
        ids.push((s as UnassignedPickup).id);
      }
    } else if (s.kind === "pickup_group") {
      for (const p of s.pickups) {
        if (p.status !== PickupStatus.COMPLETED) {
          ids.push(p.id);
        }
      }
    }
  }
  return ids;
}

function collectPickupIdsForUnassign(stops: Array<Stop | UnassignedStopGroup>): string[] {
  const ids: string[] = [];
  for (const s of stops) {
    if ("kind" in s && s.kind === "pickup_group") {
      for (const p of s.pickups) {
        if (p.status !== PickupStatus.COMPLETED) {
          ids.push(p.id);
        }
      }
    }
  }
  return ids;
}

type DriverToDriverReassignPayload = {
  unassign: PickupAssignRequestModel[];
  assign: PickupAssignRequestModel[];
};

function isUnassignedStopGroupRow(s: Stop | UnassignedStopGroup): s is UnassignedStopGroup {
  return !("kind" in s) && "pickups" in s && Array.isArray((s as UnassignedStopGroup).pickups);
}

function isStopSelectedForDrag(s: Stop | UnassignedStopGroup, selectedIds: Set<string>): boolean {
  if (isUnassignedStopGroupRow(s)) {
    return s.pickups.length > 0 && s.pickups.every((p) => selectedIds.has(p.id));
  }
  return selectedIds.has(s.id);
}

/**
 * Computes next column state and optional `changePickups` payload for cross-column moves.
 * Returns null when the drop is invalid (e.g. manifest delivery rows cannot move between columns).
 * Driver → driver: `driverToDriverReassign` is set (unassign then assign in sequence); `apiPayload` is null.
 */
function computeDragMutation(
  prev: DispatchColumns,
  selectedTaskIds: Set<string>,
  result: DropResult
): {
  next: DispatchColumns;
  apiPayload: PickupAssignRequestModel[] | null;
  driverToDriverReassign: DriverToDriverReassignPayload | null;
} | null {
  if (!result.destination) return null;

  const sourceDroppableId = result.source.droppableId;
  const destDroppableId = result.destination.droppableId;
  const sourceIndex = result.source.index;
  const destIndex = result.destination.index;

  if (sourceDroppableId === destDroppableId && sourceIndex === destIndex) return null;

  const findColumn = (droppableId: string): "drivers" | "unassigned" | null => {
    if (prev.drivers.some((d) => d.id === droppableId)) return "drivers";
    if (prev.unassigned.some((r) => r.id === droppableId)) return "unassigned";
    return null;
  };

  const getStops = (droppableId: string): Array<Stop | UnassignedStopGroup> => {
    const driver = prev.drivers.find((d) => d.id === droppableId);
    if (driver) return [...driver.stops];
    const route = prev.unassigned.find((r) => r.id === droppableId);
    if (route) return [...route.stops];
    return [];
  };

  const updateStops = (
    groups: DriverGroup[] | UnassignedRoute[],
    droppableId: string,
    newStops: Array<Stop | UnassignedStopGroup>
  ) =>
    (groups as any[]).map((group) =>
      group.id === droppableId ? { ...group, stops: newStops } : group
    );

  const sourceStops = getStops(sourceDroppableId);
  const draggedStop = sourceStops[sourceIndex];
  if (!draggedStop) return null;

  const isDraggedSelected = isStopSelectedForDrag(draggedStop, selectedTaskIds);
  const stopIdsToMove = isDraggedSelected
    ? new Set(sourceStops.filter((s) => isStopSelectedForDrag(s, selectedTaskIds)).map((s) => s.id))
    : new Set([draggedStop.id]);

  const stopsToMove = sourceStops.filter((s) => stopIdsToMove.has(s.id));
  const newSourceStops = sourceStops.filter((s) => !stopIdsToMove.has(s.id));

  const sourceColumn = findColumn(sourceDroppableId);
  const destColumn = findColumn(destDroppableId);
  if (!sourceColumn || !destColumn) return null;

  const isCrossColumn = sourceDroppableId !== destDroppableId;
  let apiPayload: PickupAssignRequestModel[] | null = null;
  let driverToDriverReassign: DriverToDriverReassignPayload | null = null;

  if (isCrossColumn) {
    const hasManifest = stopsToMove.some(
      (s): s is Stop => "kind" in s && (s as Stop).kind === "manifest_stop"
    );
    if (hasManifest) return null;

    if (destColumn === "drivers") {
      const destDriver = prev.drivers.find((d) => d.id === destDroppableId);
      if (!destDriver) return null;
      const pickupIds = collectPickupIdsForAssign(stopsToMove);
      if (pickupIds.length === 0) return null;
      const driverIdNum = Number(destDriver.driverId);
      if (Number.isNaN(driverIdNum)) return null;

      const isDriverToDriver =
        sourceColumn === "drivers" && sourceDroppableId !== destDroppableId;
      if (isDriverToDriver) {
        driverToDriverReassign = {
          unassign: pickupIds.map((pickupId) => ({
            pickupId,
            driverId: null,
            status: PickupStatus.UNASSIGNED,
          })),
          assign: pickupIds.map((pickupId) => ({
            pickupId,
            driverId: driverIdNum,
            status: PickupStatus.ASSIGNED,
          })),
        };
      } else {
        apiPayload = pickupIds.map((pickupId) => ({
          pickupId,
          driverId: driverIdNum,
          status: PickupStatus.ASSIGNED,
        }));
      }
    } else if (destColumn === "unassigned" && sourceColumn === "drivers") {
      const pickupIds = collectPickupIdsForUnassign(stopsToMove);
      if (pickupIds.length === 0) return null;
      apiPayload = pickupIds.map((pickupId) => ({
        pickupId,
        driverId: null,
        status: PickupStatus.UNASSIGNED,
      }));
    }
  }

  if (sourceDroppableId === destDroppableId) {
    const removedBefore = sourceStops
      .slice(0, destIndex)
      .filter((s) => stopIdsToMove.has(s.id)).length;

    const insertAt = Math.max(0, destIndex - removedBefore);
    const reordered = [...newSourceStops];
    reordered.splice(insertAt, 0, ...stopsToMove);

    const sourceCol = findColumn(sourceDroppableId);
    if (!sourceCol) return null;

    return {
      next: {
        ...prev,
        [sourceCol]: updateStops(prev[sourceCol], sourceDroppableId, reordered),
      },
      apiPayload: null,
      driverToDriverReassign: null,
    };
  }

  const destStops = getStops(destDroppableId);
  destStops.splice(destIndex, 0, ...stopsToMove);

  let updatedDrivers = [...prev.drivers];
  let updatedUnassigned = [...prev.unassigned];

  if (sourceColumn === "drivers") {
    updatedDrivers = updateStops(updatedDrivers, sourceDroppableId, newSourceStops);
  } else {
    updatedUnassigned = updateStops(updatedUnassigned, sourceDroppableId, newSourceStops);
  }

  if (destColumn === "drivers") {
    updatedDrivers = updateStops(updatedDrivers, destDroppableId, destStops);
  } else {
    updatedUnassigned = updateStops(updatedUnassigned, destDroppableId, destStops);
  }

  return {
    next: {
      drivers: updatedDrivers,
      unassigned: updatedUnassigned,
    },
    apiPayload,
    driverToDriverReassign,
  };
}

/** Resolves unassigned stop group ids to underlying pickup ids for assign API / counts. */
const PICKUP_ASSIGNMENT_AUTO_REFRESH_MS = 120_000;
const PICKUP_ASSIGNMENT_RELATIVE_TIME_TICK_MS = 30_000;

const PickupAssignment: React.FC<{}> = (props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const history = useHistory();
  const params = useParams<{ [key: string]: string }>();
  const userState = useSelector(userSelector);
  const activeTerminalId = userState.activeTerminal?.id;
  const hasPickupAssignmentV2Access = isPickupAssignmentV2Allowed(activeTerminalId);
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const unassignedPickupState: UnassignedPickupState = useSelector(unassignedPickupSelector);
  const unassignedRoutes = useMemo(() => {
    const loadedFor = unassignedPickupState.loadedForTerminalId;
    if (
      activeTerminalId != null &&
      loadedFor != null &&
      loadedFor !== activeTerminalId
    ) {
      return [];
    }
    return mapUnassignedRoutes(unassignedPickupState);
  }, [unassignedPickupState, activeTerminalId]);

  const [pickupDetailsNumber, setPickupDetailsNumber] = useState<string>("");
  const pickupDetails = pickupDetailsState.pickup && getPickupDetails(pickupDetailsState.pickup);

  const onShowPickupDetails = useCallback(
    (pickupNumber: string) => {
      if (pickupDetailsNumber !== pickupNumber) {
        setPickupDetailsNumber(pickupNumber);
        dispatch(getPickup(pickupNumber));
      }
    },
    [dispatch, pickupDetailsNumber]
  );

  const {
    drivers: driversFromApi,
    loading: driversColumnLoading,
    error: driversColumnError,
    refetch: refetchDriversColumn,
  } = usePickupAssignmentDriversColumn(
    hasPickupAssignmentV2Access ? activeTerminalId : undefined
  );

  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [, setRelativeTimeTick] = useState(0);
  const didSetInitialRefreshRef = useRef(false);

  const refreshPickupAssignment = useCallback(async () => {
    if (!hasPickupAssignmentV2Access || activeTerminalId == null) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchDriversColumn(),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_STATIC_ROUTES] }),
      ]);
      setLastRefreshedAt(Date.now());
    } finally {
      setIsRefreshing(false);
    }
  }, [hasPickupAssignmentV2Access, activeTerminalId, refetchDriversColumn, queryClient]);

  useEffect(() => {
    didSetInitialRefreshRef.current = false;
    setLastRefreshedAt(null);
  }, [activeTerminalId]);

  useEffect(() => {
    if (!hasPickupAssignmentV2Access || activeTerminalId == null) return;
    if (driversColumnError) return;
    if (!driversColumnLoading && !didSetInitialRefreshRef.current) {
      didSetInitialRefreshRef.current = true;
      setLastRefreshedAt(Date.now());
    }
  }, [
    hasPickupAssignmentV2Access,
    activeTerminalId,
    driversColumnLoading,
    driversColumnError,
  ]);

  useEffect(() => {
    if (!hasPickupAssignmentV2Access || activeTerminalId == null) return;
    const id = window.setInterval(() => {
      void refreshPickupAssignment();
    }, PICKUP_ASSIGNMENT_AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [hasPickupAssignmentV2Access, activeTerminalId, refreshPickupAssignment]);

  useEffect(() => {
    if (lastRefreshedAt == null) return;
    const id = window.setInterval(() => {
      setRelativeTimeTick((n) => n + 1);
    }, PICKUP_ASSIGNMENT_RELATIVE_TIME_TICK_MS);
    return () => clearInterval(id);
  }, [lastRefreshedAt]);

  const lastRefreshedLabel =
    lastRefreshedAt == null ? null : moment(lastRefreshedAt).fromNow();

  const assignDriverSelectOptions = useMemo(
    () =>
      driversFromApi.map((d) => ({
        value: d.driverId,
        label: d.name,
      })),
    [driversFromApi]
  );

  const [dispatchColumns, setDispatchColumns] = useState<DispatchColumns>({
    drivers: [],
    unassigned: unassignedRoutes,
  });

  useEffect(() => {
    setDispatchColumns((prev) => ({ ...prev, unassigned: unassignedRoutes }));
  }, [unassignedRoutes]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [expandedDrivers, setExpandedDrivers] = useState<{ value: boolean; version: number } | null>({
    value: false,
    version: 0,
  });
  const [expandedUnassigned, setExpandedUnassigned] = useState<{ value: boolean; version: number } | null>({
    value: false,
    version: 0,
  });
  const [driverNestExpand, setDriverNestExpand] = useState({ open: false, version: 0 });
  const [unassignedNestExpand, setUnassignedNestExpand] = useState({ open: false, version: 0 });
  const mapSync = usePickupAssignmentMapSyncState();
  const [mapSelectedStopId, setMapSelectedStopId] = useState<string | null>(null);
  const [mapSelectedDriverId, setMapSelectedDriverId] = useState<string | null>(null);
  const [driverColumnPrimarySearch, setDriverColumnPrimarySearch] = useState("");
  const [driverColumnSecondarySearch, setDriverColumnSecondarySearch] = useState("");

  const driversListForDisplay = useMemo(
    () =>
      filterDriverGroupsBySearch(
        dispatchColumns.drivers,
        driverColumnPrimarySearch,
        driverColumnSecondarySearch
      ),
    [dispatchColumns.drivers, driverColumnPrimarySearch, driverColumnSecondarySearch]
  );

  const driversColumnTabCounts = useMemo(
    () => ({
      all: driversListForDisplay.length,
      atRisk: filterDriverGroupsByTab(driversListForDisplay, "atRisk").length,
      late: filterDriverGroupsByTab(driversListForDisplay, "late").length,
    }),
    [driversListForDisplay]
  );

  /** From API `unassignedTabCounts` when present; else derive "All" total from loaded routes. */
  const unassignedColumnTabCounts = useMemo(() => {
    const loadedFor = unassignedPickupState.loadedForTerminalId;
    if (
      activeTerminalId != null &&
      loadedFor != null &&
      loadedFor !== activeTerminalId
    ) {
      return { all: 0, atRisk: 0, late: 0 };
    }
    const fromApi = unassignedPickupState.unassignedTabCounts;
    if (fromApi) {
      return fromApi;
    }
    const fallbackAll = dispatchColumns.unassigned.reduce((sum, route) => sum + route.count, 0);
    return { all: fallbackAll, atRisk: 0, late: 0 };
  }, [
    unassignedPickupState.unassignedTabCounts,
    unassignedPickupState.loadedForTerminalId,
    dispatchColumns.unassigned,
    activeTerminalId,
  ]);

  const driversListExpandSeqRef = useRef(0);
  const unassignedListExpandSeqRef = useRef(0);

  useEffect(() => {
    setSelectedTaskIds(new Set());
  }, [activeTerminalId]);

  useEffect(() => {
    setMapSelectedStopId(null);
    setMapSelectedDriverId(null);
  }, [activeTerminalId]);

  const mapHighlightedIds = useMemo(
    () => (mapSelectedStopId ? new Set([mapSelectedStopId]) : new Set<string>()),
    [mapSelectedStopId]
  );

  const handleStopMapFocus = useCallback(
    (stopId: string) => {
      setMapSelectedStopId(stopId);
      mapSync.requestFocusStop(stopId);
    },
    [mapSync]
  );

  useEffect(() => {
    if (hasPickupAssignmentV2Access) {
      mixpanel.track("page_viewed", { page: "pickup_management_v2" });
    }
  }, [hasPickupAssignmentV2Access, userState.activeTerminal?.id]);

  useEffect(() => {
    if (!hasPickupAssignmentV2Access) {
      const terminalId = params[terminalNumberParamName] ?? activeTerminalId;
      const homePath = terminalId != null ? `/${terminalId}${Routes.home}` : Routes.home;
      history.replace(homePath);
    }
  }, [hasPickupAssignmentV2Access, history, params, activeTerminalId]);

  useEffect(() => {
    setDispatchColumns((prev) => ({ ...prev, drivers: driversFromApi }));
  }, [driversFromApi]);

  const { assignPickupsAsync } = useAssignDrivers();

  /** Unassigned column: selection is pickup ids only. */
  const expandedSelectedPickupIds = useMemo(
    () => Array.from(selectedTaskIds),
    [selectedTaskIds]
  );

  const handleTogglePickupSelection = useCallback((pickupId: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      next.has(pickupId) ? next.delete(pickupId) : next.add(pickupId);
      return next;
    });
  }, []);

  const handleUnassignedStopRowToggle = useCallback((task: UnassignedStopGroup) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      const allSelected = task.pickups.every((p) => next.has(p.id));
      task.pickups.forEach((p) => {
        if (allSelected) next.delete(p.id);
        else next.add(p.id);
      });
      return next;
    });
  }, []);

  const handleToggleSelectAllUnassigned = useCallback(
    (droppableId: string, checked: boolean) => {
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        const route = dispatchColumns.unassigned.find((r) => r.id === droppableId);
        if (!route) return prev;
        route.stops.forEach((stop) => {
          stop.pickups.forEach((p) => {
            if (checked) next.add(p.id);
            else next.delete(p.id);
          });
        });
        return next;
      });
    },
    [dispatchColumns.unassigned]
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const computed = computeDragMutation(dispatchColumns, selectedTaskIds, result);
      if (!computed) return;

      const previousColumns = dispatchColumns;
      setDispatchColumns(computed.next);

      const clearSelection = () => setSelectedTaskIds(new Set());
      const revertColumns = () => setDispatchColumns(previousColumns);

      if (computed.driverToDriverReassign) {
        const { unassign, assign } = computed.driverToDriverReassign;
        void assignPickupsAsync(unassign)
          .then(() => assignPickupsAsync(assign))
          .then(() => clearSelection())
          .catch(() => {
            revertColumns();
          });
      } else if (computed.apiPayload?.length) {
        void assignPickupsAsync(computed.apiPayload)
          .then(() => clearSelection())
          .catch(() => {
            revertColumns();
          });
      } else {
        clearSelection();
      }
    },
    [dispatchColumns, selectedTaskIds, assignPickupsAsync]
  );

  const handleDriversExpandAll = () => {
    driversListExpandSeqRef.current += 1;
    setExpandedDrivers({ value: true, version: driversListExpandSeqRef.current });
    setDriverNestExpand((p) => ({ open: true, version: p.version + 1 }));
  };

  const handleDriversCollapseAll = () => {
    driversListExpandSeqRef.current += 1;
    setExpandedDrivers({ value: false, version: driversListExpandSeqRef.current });
    setDriverNestExpand((p) => ({ open: false, version: p.version + 1 }));
  };

  const handleUnassignedExpandAll = useCallback(() => {
    unassignedListExpandSeqRef.current += 1;
    setExpandedUnassigned({
      value: true,
      version: unassignedListExpandSeqRef.current,
    });
    setUnassignedNestExpand((p) => ({ open: true, version: p.version + 1 }));
  }, []);

  const handleUnassignedCollapseAll = useCallback(() => {
    unassignedListExpandSeqRef.current += 1;
    setExpandedUnassigned({
      value: false,
      version: unassignedListExpandSeqRef.current,
    });
    setUnassignedNestExpand((p) => ({ open: false, version: p.version + 1 }));
  }, []);

  const renderTaskCard = (task: Stop, _isSelected: boolean) => {
    if (task.kind === "manifest_stop") {
      return <ManifestStopGroup task={task} onStopMapFocus={handleStopMapFocus} />;
    }
    if (task.kind === "pickup_group") {
      return (
        <PickupGroupCard
          task={task}
          onSelectPickup={onShowPickupDetails}
          onStopMapFocus={handleStopMapFocus}
        />
      );
    }
    return null;
  };

  const renderUnassignedStopCard = (task: UnassignedStopGroup, _isSelected: boolean) => (
    <UnassignedPickupStopGroup
      task={task}
      onSelectPickup={onShowPickupDetails}
      selectedPickupIds={selectedTaskIds}
      onTogglePickupSelection={handleTogglePickupSelection}
      onStopMapFocus={handleStopMapFocus}
    />
  );

  const getStatusColor = (driverStatus: string) => {
    return statusColorMapping[driverStatus as keyof typeof statusColorMapping] || 'black';
  };

  const getStatusText = (driverStatus: string) => {
    return driverStatus.toLowerCase().capitalize().split("_").join(" ");
  };

  const driverDetails = (driver: DriverGroup) => {
    const {
      trailerLabel,
      manifestLabel,
      driverStatus,
      lastLocationLine,
      numberOfPickups,
      numberOfDeliveries,
      progressLabel,
    } = driver;
    const pickupText = getPluralOrSingular(numberOfPickups, "Pickup", "Pickups");
    const deliveryText = getPluralOrSingular(numberOfDeliveries, "Delivery", "Deliveries");
    const progressDisplay =
      progressLabel === "—" ? progressLabel : `Progress ${progressLabel}`;

    return (
      <div className="driver-collapsible-summary">
        <div className="driver-collapsible-summary__grid">
          <div className="driver-collapsible-summary__row driver-collapsible-summary__row--equipment">
            <span>{trailerLabel}</span>
            <span className="driver-collapsible-summary__sep" aria-hidden>
              ·
            </span>
            <span>{manifestLabel}</span>
            <span className="driver-collapsible-summary__sep" aria-hidden>
              ·
            </span>
            <span
              className="driver-collapsible-summary__duty"
              style={{ color: getStatusColor(driverStatus) }}
            >
              {getStatusText(driverStatus)}
            </span>
          </div>
          <div
            className="driver-collapsible-summary__location"
            title={lastLocationLine}
          >
            {lastLocationLine}
          </div>
          <div className="driver-collapsible-summary__row driver-collapsible-summary__row--stats">
            <span>
              {numberOfPickups} {pickupText}
            </span>
            <span className="driver-collapsible-summary__sep" aria-hidden>
              ·
            </span>
            <span>
              {numberOfDeliveries} {deliveryText}
            </span>
            <span className="driver-collapsible-summary__sep" aria-hidden>
              ·
            </span>
            <span>{progressDisplay}</span>
          </div>
        </div>
      </div>
    );
  };

  /** Single-line header when the driver row is collapsed. */
  const driverCollapsedHeader = (driver: DriverGroup) => {
    const {
      trailerLabel,
      manifestLabel,
      driverStatus,
      deliveriesCompleted,
      numberOfDeliveries,
      pickupsCompleted,
      numberOfPickups,
      lastLocationLine,
    } = driver;
    const hasTractor = trailerLabel !== "—";
    const hasManifest = manifestLabel !== "—";
    const hasLocation = lastLocationLine !== "—";

    return (
      <div className="driver-collapsible-summary driver-collapsible-summary--single-line">
        <div className="driver-collapsible-summary__single-line-main">
          <span className="driver-collapsible-title__name">{driver.name}</span>
          {hasTractor && (
            <>
              <span className="driver-collapsible-summary__sep" aria-hidden>
                ·
              </span>
              <span>{trailerLabel}</span>
            </>
          )}
          {hasManifest && (
            <>
              <span className="driver-collapsible-summary__sep" aria-hidden>
                ·
              </span>
              <span>{manifestLabel}</span>
            </>
          )}
          <span className="driver-collapsible-summary__sep" aria-hidden>
            ·
          </span>
          <span
            className="driver-collapsible-summary__duty"
            style={{ color: getStatusColor(driverStatus) }}
          >
            {getStatusText(driverStatus)}
          </span>
        </div>
        <div className="driver-collapsible-summary__single-line-trailing">
          <div className="driver-collapsible-summary__stat-tags">
            <span className="pa-manifest-stop__type-tag pa-manifest-stop__type-tag--delivery">
              <FontAwesomeIcon icon={faArrowDown} className="pa-manifest-stop__fa-icon" />
              {`${deliveriesCompleted}/${numberOfDeliveries}`}
            </span>
            <span className="pa-pickup-group__type-tag pa-pickup-group__type-tag--pickup">
              <FontAwesomeIcon icon={faArrowUp} className="pa-pickup-group__fa-icon" />
              {`${pickupsCompleted}/${numberOfPickups}`}
            </span>
          </div>
          {hasLocation ? (
            <Popup
              trigger={
                <span
                  className="driver-collapsible-summary__location-pin"
                  role="button"
                  tabIndex={0}
                  aria-label={`Last known location: ${lastLocationLine}`}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden />
                </span>
              }
              on="hover"
              position="top center"
              nested
              closeOnDocumentClick
              arrow={false}
              offsetY={2}
              contentStyle={{
                padding: 0,
                border: "none",
                background: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="driver-collapsible-summary__location-tooltip">
                {lastLocationLine}
              </div>
            </Popup>
          ) : (
            <Popup
              trigger={
                <span
                  className="driver-collapsible-summary__location-pin driver-collapsible-summary__location-pin--unavailable"
                  aria-label="Location not available"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden />
                </span>
              }
              on="hover"
              position="top center"
              nested
              closeOnDocumentClick
              arrow={false}
              offsetY={2}
              contentStyle={{
                padding: 0,
                border: "none",
                background: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="driver-collapsible-summary__location-tooltip">
                Location not available
              </div>
            </Popup>
          )}
        </div>
      </div>
    );
  };

  const titleDriverComponent = (driver: DriverGroup, isExpanded: boolean) =>
    isExpanded ? (
      <div className="driver-collapsible-title">
        <span className="driver-collapsible-title__name">{driver.name}</span>
        <span className="driver-collapsible-title__id">{driver.driverId}</span>
      </div>
    ) : (
      driverCollapsedHeader(driver)
    );

  const titleUnassignedComponent = (route: UnassignedRoute) => (
    <>
      {route.name} ({route.count})
    </>
  );

  const renderDriversList = (tab: DriverTabFilter) => {
    const driversForTab = filterDriverGroupsByTab(driversListForDisplay, tab);
    return (
      <>
        <div className="xgs-pickup-assignment-sticky-filters xgs-pickup-assignment-sticky-filters--drivers">
          <FilterComponent
            primaryFilterPlaceholder="Search drivers, addresses.."
            secondaryFilterPlaceholder="Pickup #, PRO , BOL , PO.."
            selectedTab={tab}
            shouldFetchStaticRoutes={false}
            showExpandCollapse={false}
            primaryFilterValue={driverColumnPrimarySearch}
            onPrimaryFilterChange={setDriverColumnPrimarySearch}
            secondaryFilterValue={driverColumnSecondarySearch}
            onSecondaryFilterChange={setDriverColumnSecondarySearch}
          />
        </div>
        {driversColumnError && (
          <div className="xgs-pickup-delivery-list__error" role="alert">
            {driversColumnError}
          </div>
        )}
        {driversColumnLoading && dispatchColumns.drivers.length === 0 && !driversColumnError && (
          <div className="xgs-pickup-delivery-list__loading">Loading drivers…</div>
        )}
        <DriverColumnNestExpandContext.Provider value={driverNestExpand}>
          <div className="xgs-pickup-delivery-list xgs-pickup-delivery-list--drivers">
            {driversForTab.map((driver) => (
              <DraggableList
                key={driver.id}
                droppableId={driver.id}
                title={(isExpanded) => titleDriverComponent(driver, isExpanded)}
                items={driver.stops}
                renderTask={renderTaskCard}
                selectedIds={selectedTaskIds}
                highlightedIds={mapHighlightedIds}
                mapSyncExpand={
                  mapSync.focusDriverDroppableId === driver.id
                    ? { version: mapSync.focusDriverVersion }
                    : undefined
                }
                onSectionHeaderClick={() => {
                  setMapSelectedDriverId(driver.id);
                  setMapSelectedStopId(null);
                  mapSync.requestFocusDriver(driver.id);
                }}
                emptyMessage="No assigned deliveries"
                hideEmptyWhenCollapsed
                expandedAll={
                  Boolean(expandedDrivers?.value) &&
                  driverHasAssignedPickupOrDelivery(driver)
                }
                expandVersion={expandedDrivers?.version}
                description={(isExpanded) => (isExpanded ? driverDetails(driver) : null)}
              />
            ))}
          </div>
        </DriverColumnNestExpandContext.Provider>
      </>
    );
  };

  const renderUnassignedList = (selectedTab: DriverTabFilter) => (
    <>
      <div className="xgs-pickup-assignment-sticky-filters xgs-pickup-assignment-sticky-filters--unassigned">
        <FilterComponent
          primaryFilterPlaceholder="Search addresses, shippers.."
          secondaryFilterPlaceholder="Pickup #, BOL, PO.."
          selectedTab={selectedTab}
          shouldFetchStaticRoutes={true}
          showExpandCollapse={false}
        />
        {expandedSelectedPickupIds.length > 0 && (
          <AssignDriverBar
            selectedPickupIds={expandedSelectedPickupIds}
            selectedCount={expandedSelectedPickupIds.length}
            drivers={assignDriverSelectOptions}
          />
        )}
      </div>
      <UnassignedStopNestExpandContext.Provider value={unassignedNestExpand}>
        <div className="xgs-pickup-delivery-list xgs-pickup-delivery-list--unassigned">
          {dispatchColumns.unassigned?.map((route: UnassignedRoute) => (
            <DraggableList
              key={route.id}
              droppableId={route.id}
              title={() => titleUnassignedComponent(route)}
              items={route.stops}
              renderTask={renderUnassignedStopCard}
              selectedIds={selectedTaskIds}
              highlightedIds={mapHighlightedIds}
              getItemSelected={(task, ids) => {
                const t = task as UnassignedStopGroup;
                return (
                  t.pickups.length > 0 && t.pickups.every((p) => ids.has(p.id))
                );
              }}
              onToggleItem={(task) => handleUnassignedStopRowToggle(task as UnassignedStopGroup)}
              onToggleSelectAll={(checked) => handleToggleSelectAllUnassigned(route.id, checked)}
              emptyMessage="No unassigned pickups"
              hideEmptyWhenCollapsed
              expandedAll={expandedUnassigned?.value}
              expandVersion={expandedUnassigned?.version}
            />
          ))}
        </div>
      </UnassignedStopNestExpandContext.Provider>
    </>
  );

  const splitPanelInitialWidths = useMemo(() => [33, 33, 33], []);

  if (!hasPickupAssignmentV2Access) {
    return null;
  }

  const splitSections: SplitPanelSection[] = [
    {
      key: "drivers",
      style: { padding: 16, background: "#f7f7f7" },
      content: (
        <div className="xgs-pickup-assignment-split-column">
          <div className="xgs-pickup-delivery-list__title-row">
            <div className="xgs-pickup-delivery-list__title">
              Drivers ({driversListForDisplay.length})
            </div>
            <div className="xgs-pickup-delivery-list__title-actions">
              <button
                type="button"
                className="xgs-pickup-delivery-list__icon-btn"
                onClick={handleDriversExpandAll}
                title="Expand all"
                aria-label="Expand all"
              >
                <ExpandAllIcon color="#2F80ED" aria-hidden />
              </button>
              <button
                type="button"
                className="xgs-pickup-delivery-list__icon-btn"
                onClick={handleDriversCollapseAll}
                title="Collapse all"
                aria-label="Collapse all"
              >
                <CollapseAllIcon color="#2F80ED" aria-hidden />
              </button>
            </div>
          </div>
          <TabsComponent
            variant="drivers"
            renderDriversPanel={(tab) => renderDriversList(tab)}
            tabCounts={driversColumnTabCounts}
          />
        </div>
      ),
    },
    {
      key: "unassigned",
      style: { padding: 16, background: "#f0f0f0" },
      content: (
        <div className="xgs-pickup-assignment-split-column">
          <div className="xgs-pickup-delivery-list__title-row">
            <div className="xgs-pickup-delivery-list__title">
              Unassigned pickups ({unassignedColumnTabCounts.all})
            </div>
            <div className="xgs-pickup-delivery-list__title-actions">
              <button
                type="button"
                className="xgs-pickup-delivery-list__icon-btn"
                onClick={handleUnassignedExpandAll}
                title="Expand all"
                aria-label="Expand all"
              >
                <ExpandAllIcon color="#2F80ED" aria-hidden />
              </button>
              <button
                type="button"
                className="xgs-pickup-delivery-list__icon-btn"
                onClick={handleUnassignedCollapseAll}
                title="Collapse all"
                aria-label="Collapse all"
              >
                <CollapseAllIcon color="#2F80ED" aria-hidden />
              </button>
            </div>
          </div>
          <TabsComponent
            variant="unassigned"
            tabCounts={unassignedColumnTabCounts}
            renderUnassignedPanel={(tab) => renderUnassignedList(tab)}
          />
        </div>
      ),
    },
    {
      key: "map",
      style: { padding: 0, background: "#e8e8e8", minHeight: 420 },
      content: (
        <div className="xgs-pickup-assignment-split-column xgs-pickup-assignment-split-column--map">
          <div className="xgs-pickup-delivery-list__title-row">
            <div className="xgs-pickup-delivery-list__title">Map</div>
          </div>
          <PickupAssignmentMapPanel
            dispatchColumns={dispatchColumns}
            driverOptions={assignDriverSelectOptions}
            selectedStopId={mapSelectedStopId}
            selectedDriverDroppableId={mapSelectedDriverId}
            onSelectStopOnMap={(stopId, driverDroppableId) => {
              setMapSelectedStopId(stopId);
              if (driverDroppableId) setMapSelectedDriverId(driverDroppableId);
            }}
            onSelectDriverOnMap={(droppableId) => {
              setMapSelectedDriverId(droppableId);
              setMapSelectedStopId(null);
            }}
            mapSync={mapSync}
          />
        </div>
      ),
    },
  ];

  return (
    <ContentContainer
      mods={{ "full-width": true, "screen-height": true }}
      titleComponent={
        <ContentContainerToolbar
          title="Pickup & Delivery Assignment (V2)"
          pageId={PAGE_IDS.PICKUP_ASSIGNMENT_V2}
          mods={{ "full-width": true }}
        >
          <div className="xgs-pickup-assignment__refresh-toolbar">
            <span className="xgs-pickup-assignment__last-refreshed">
              Last refreshed {lastRefreshedLabel ?? "—"}
            </span>
            <ReloadButton
              title="Refresh"
              spinner={isRefreshing}
              disabled={isRefreshing}
              onClick={() => {
                void refreshPickupAssignment();
              }}
            />
          </div>
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-pickup-assignment-layout">
        <PickupAssignmentMapSyncContext.Provider value={mapSync}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <SplitPanel initialWidths={splitPanelInitialWidths} sections={splitSections} />
          </DragDropContext>
        </PickupAssignmentMapSyncContext.Provider>
      </div>
      <PickupDetails
        readonly
        isOpen={!!pickupDetailsNumber}
        pickup={pickupDetails}
        onClose={() => setPickupDetailsNumber("")}
        isLoading={
          pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "GET_PICKUP"
        }
        error={
          pickupDetailsState.requestCreator === "GET_PICKUP"
            ? pickupDetailsState.requestError
            : ""
        }
      />
    </ContentContainer>
  );
};

export default PickupAssignment;
